import translations from "@/config/translations";
import useFetch from "@/hooks/useFetch";
import useLangStore from "@/hooks/useLangStore";
import { useState } from "react";
import "./styles.css";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ChallengeTargetItem {
    _id: string;
    name: string;
    picture: string;
}

export interface ChallengeTargetPopupDetails {
    type: "product" | "category";
    onSelect: (item: ChallengeTargetItem) => void;
}

interface Props {
    onClose: () => void;
    details?: ChallengeTargetPopupDetails;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function ChallengeTargetPopup({ onClose, details }: Props) {
    const { lang } = useLangStore();
    const t = translations[lang ?? "ar"];

    const type = details?.type ?? "product";
    const endpoint = type === "product" ? "challenges/products" : "challenges/categories";
    const dataKey = type === "product" ? "products" : "categories";

    const [selected, setSelected] = useState<ChallengeTargetItem | null>(null);
    const [search, setSearch] = useState("");

    const { data, loading } = useFetch(endpoint);
    const items: ChallengeTargetItem[] = data?.data?.[dataKey] ?? [];
    const filteredItems = search.trim() ? items.filter((item) => item.name.toLowerCase().includes(search.trim().toLowerCase())) : items;

    const handleChoose = () => {
        if (selected) {
            details?.onSelect(selected);
            onClose();
        }
    };

    const title = type === "product" ? t.challengeForm.selectProduct : t.challengeForm.selectCategory;

    return (
        <div className="challenge-target-popup">
            <div className="ctp-header">
                <h2 className="ctp-title">{title}</h2>
                <button type="button" className="ctp-close" onClick={onClose} aria-label="Close">
                    ×
                </button>
            </div>

            <div className="ctp-search">
                <input
                    className="ctp-search-input"
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t.actionsBar.search}
                    autoFocus
                />
            </div>

            <div className="ctp-body">
                {loading ? (
                    <div className="ctp-loading">
                        <span className="ctp-spinner" />
                    </div>
                ) : filteredItems.length === 0 ? (
                    <div className="ctp-empty">{t.table.empty}</div>
                ) : (
                    <div className="ctp-grid">
                        {filteredItems.map((item) => (
                            <button
                                key={item._id}
                                type="button"
                                className={`ctp-card${selected?._id === item._id ? " ctp-card--selected" : ""}`}
                                onClick={() => setSelected(item)}
                            >
                                <div className="ctp-card-img">
                                    <img src={item.picture} alt={item.name} />
                                </div>
                                <span className="ctp-card-name">{item.name}</span>
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <div className="ctp-footer">
                <button type="button" className="ctp-btn ctp-btn--cancel" onClick={onClose}>
                    {t.challengeForm.cancel}
                </button>
                <button type="button" className="ctp-btn ctp-btn--choose" onClick={handleChoose} disabled={!selected}>
                    {t.challengeForm.choose}
                </button>
            </div>
        </div>
    );
}
